import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  ChevronRight, 
  Clock, 
  CheckCircle2, 
  XCircle,
  Lightbulb,
  BarChart3,
  Lock,
  Loader2,
  Sparkles,
  X
} from "lucide-react";
import QuestionImage from "@/components/QuestionImage";
import { trackEvent } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const PENDING_KEY = "adcprep_pending_diagnostic";

const DiagnosticPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [reveals, setReveals] = useState({});
  const [showGate, setShowGate] = useState(false); // anonymous paywall before results
  const [showResult, setShowResult] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [pendingScores, setPendingScores] = useState(null);

  useEffect(() => {
    const fetchDiagnostic = async () => {
      try {
        const [questionsData, subjectsData] = await Promise.all([
          api.get("/questions/diagnostic"),
          api.get("/subjects")
        ]);
        setQuestions(questionsData.questions);
        setSubjects(subjectsData.subjects);
        trackEvent("full_diagnostic_started", { question_count: questionsData.questions?.length || 0 });
      } catch (error) {
        console.error("Failed to fetch diagnostic:", error);
        toast.error("Failed to load diagnostic questions");
      } finally {
        setLoading(false);
      }
    };

    fetchDiagnostic();
  }, []);

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  const handleSelectOption = (optionId) => {
    if (submitted) return;
    setSelectedOption(optionId);
  };

  const handleSubmitAnswer = async () => {
    if (!selectedOption) return;

    setSubmitted(true);
    setAnswers({
      ...answers,
      [currentQuestion.id]: selectedOption
    });
    // Reveal the correct answer server-side (one question at a time)
    try {
      const res = await api.post("/diagnostic/check", {
        question_id: currentQuestion.id,
        selected_option_id: selectedOption,
      });
      setReveals((prev) => ({ ...prev, [currentQuestion.id]: res }));
    } catch (e) {
      // non-blocking
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedOption(null);
      setSubmitted(false);
    } else {
      // Complete diagnostic
      completeDiagnostic();
    }
  };

  const completeDiagnostic = async () => {
    // Calculate subject scores
    const subjectScores = {};
    const subjectCounts = {};

    questions.forEach((q) => {
      const reveal = reveals[q.id];
      const isCorrect = reveal ? reveal.is_correct : false;
      
      if (!subjectScores[q.subject_id]) {
        subjectScores[q.subject_id] = 0;
        subjectCounts[q.subject_id] = 0;
      }
      
      subjectCounts[q.subject_id]++;
      if (isCorrect) {
        subjectScores[q.subject_id]++;
      }
    });

    // Convert to percentages
    const percentageScores = {};
    Object.keys(subjectScores).forEach((subjectId) => {
      percentageScores[subjectId] = (subjectScores[subjectId] / subjectCounts[subjectId]) * 100;
    });

    // Anonymous user? → gate behind signup
    if (!user) {
      localStorage.setItem(
        PENDING_KEY,
        JSON.stringify({ scores: percentageScores, at: Date.now() })
      );
      setPendingScores(percentageScores);
      setShowGate(true);
      return;
    }

    // Authenticated → submit directly
    try {
      const response = await api.post("/diagnostic/submit", {
        subject_scores: percentageScores
      });
      setResult(response.result);
      setShowResult(true);
      trackEvent("full_diagnostic_completed", {
        overall_score: response.result?.overall_score,
        weak_count: (response.result?.weak_areas || []).length,
      });
      toast.success("Diagnostic Complete!", {
        description: "Your personalized study plan is ready"
      });
    } catch (error) {
      console.error("Failed to submit diagnostic:", error);
      toast.error("Failed to save results");
    }
  };

  // If user logs in after completing anonymous diagnostic → submit pending
  useEffect(() => {
    if (!user) return;
    const pending = localStorage.getItem(PENDING_KEY);
    if (!pending) return;
    try {
      const data = JSON.parse(pending);
      if (!data?.scores) return;
      (async () => {
        try {
          const response = await api.post("/diagnostic/submit", { subject_scores: data.scores });
          setResult(response.result);
          setShowResult(true);
          setShowGate(false);
          trackEvent("full_diagnostic_completed", {
            overall_score: response.result?.overall_score,
            weak_count: (response.result?.weak_areas || []).length,
            source: "post_signup",
          });
          localStorage.removeItem(PENDING_KEY);
          toast.success("Welcome! Your diagnostic results are ready");
        } catch (err) {
          console.error(err);
        }
      })();
    } catch {}
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading diagnostic assessment...</p>
        </div>
      </div>
    );
  }

  // Anonymous gate: show after finishing, before results
  if (showGate && !user) {
    const answeredCount = Object.keys(answers).length + (selectedOption ? 1 : 0);
    const previewWeak = pendingScores
      ? Object.entries(pendingScores)
          .sort((a, b) => a[1] - b[1])
          .slice(0, 2)
          .map(([id]) => {
            const s = subjects.find((x) => x.id === id);
            return s?.name || id;
          })
      : [];

    return (
      <div data-testid="diagnostic-gate" className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md w-full">
          <div className="glass-strong rounded-3xl p-8 md:p-10 border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-5">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">Your results are ready</h1>
            <p className="text-muted-foreground text-sm mb-6">
              You answered <strong className="text-foreground">{answeredCount} questions</strong>. Create a free account to see your personalised breakdown + study plan.
            </p>

            {previewWeak.length > 0 && (
              <div className="mb-6 p-4 rounded-xl bg-secondary border border-border text-left">
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Preview - your top focus areas</p>
                <div className="flex flex-wrap gap-2">
                  {previewWeak.map((name) => (
                    <span key={name} className="text-xs px-3 py-1 rounded-full bg-warning/15 text-warning border border-warning/30">
                      {name}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3 italic">
                  Sign up to see your full score breakdown for all 10 subjects.
                </p>
              </div>
            )}

            <LeadEmailForm
              previewWeak={previewWeak}
              pendingScores={pendingScores}
              onCaptured={() => navigate("/signup")}
            />
            <p className="text-xs text-muted-foreground mt-3">
              No credit card. Your answers are saved securely.
            </p>
            <button
              data-testid="diagnostic-gate-login-btn"
              onClick={() => navigate("/login")}
              className="text-xs text-primary hover:underline mt-3"
            >
              Already have an account? Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showResult && result) {
    return (
      <div data-testid="diagnostic-results" className="max-w-3xl mx-auto space-y-8">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <BarChart3 className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Diagnostic Complete!</h1>
          <p className="text-muted-foreground">Here's your personalized assessment</p>
        </div>

        {/* Overall Score */}
        <div className="glass rounded-2xl p-8 border border-border text-center">
          <p className="text-sm text-muted-foreground mb-2">Overall Score</p>
          <p className="text-5xl font-bold text-primary mb-2">{Math.round(result.overall_score)}%</p>
          <p className="text-sm text-muted-foreground">
            {result.overall_score >= 70 ? "Great foundation!" : 
             result.overall_score >= 50 ? "Good start, room to improve" : 
             "Let's focus on strengthening your knowledge"}
          </p>
        </div>

        {/* Subject Breakdown */}
        <div className="glass rounded-2xl p-6 border border-border">
          <h2 className="font-semibold mb-4">Subject Breakdown</h2>
          <div className="space-y-4">
            {Object.entries(result.subject_scores).map(([subjectId, score]) => {
              const subject = subjects.find(s => s.id === subjectId);
              const isWeak = score < 50;
              const isStrong = score >= 70;
              
              return (
                <div key={subjectId}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{subject?.name || subjectId}</span>
                    <span className={`text-sm font-bold ${isStrong ? 'text-success' : isWeak ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {Math.round(score)}%
                    </span>
                  </div>
                  <Progress 
                    value={score} 
                    className={`h-2 ${isStrong ? '[&>div]:bg-success' : isWeak ? '[&>div]:bg-destructive' : ''}`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Recommendations */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Weak Areas */}
          {result.weak_areas.length > 0 && (
            <div className="glass rounded-2xl p-6 border border-destructive/30 bg-destructive/5">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <XCircle className="w-5 h-5 text-destructive" />
                Focus Areas
              </h3>
              <ul className="space-y-2">
                {result.weak_areas.map((subjectId) => {
                  const subject = subjects.find(s => s.id === subjectId);
                  return (
                    <li key={subjectId} className="text-sm text-muted-foreground">
                      • {subject?.name || subjectId}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Strong Areas */}
          {result.strong_areas.length > 0 && (
            <div className="glass rounded-2xl p-6 border border-success/30 bg-success/5">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-success" />
                Strong Areas
              </h3>
              <ul className="space-y-2">
                {result.strong_areas.map((subjectId) => {
                  const subject = subjects.find(s => s.id === subjectId);
                  return (
                    <li key={subjectId} className="text-sm text-muted-foreground">
                      • {subject?.name || subjectId}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            data-testid="generate-plan-btn"
            size="lg"
            className="rounded-full"
            onClick={() => navigate("/study-plan")}
          >
            Generate Study Plan
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
          <Button
            data-testid="go-dashboard-btn"
            variant="outline"
            size="lg"
            className="rounded-full border-border"
            onClick={() => navigate("/dashboard")}
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="diagnostic-page" className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl md:text-2xl font-bold">Diagnostic Assessment</h1>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="w-4 h-4" />
              {currentIndex + 1} of {questions.length}
            </div>
            <button
              onClick={() => {
                if (window.confirm("Leave the diagnostic? Your progress won't be saved.")) {
                  navigate(user ? "/dashboard" : "/");
                }
              }}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              aria-label="Exit diagnostic"
              data-testid="diagnostic-exit-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Question Card */}
      {currentQuestion && (
        <div className="glass rounded-2xl p-6 md:p-8 border border-border mb-6">
          {/* Subject Tag */}
          <div className="mb-4">
            <span className="text-xs px-3 py-1 rounded-full bg-primary/20 text-primary">
              {subjects.find(s => s.id === currentQuestion.subject_id)?.name || currentQuestion.subject_id}
            </span>
          </div>

          {/* Clinical Vignette */}
          {currentQuestion.clinical_vignette && (
            <div className="p-4 rounded-xl bg-secondary border border-border mb-4">
              <p className="text-sm text-muted-foreground italic">{currentQuestion.clinical_vignette}</p>
            </div>
          )}

          {/* Question Text */}
          <QuestionImage src={currentQuestion.image_url} caption={currentQuestion.image_caption} />
          <h2 className="text-lg md:text-xl font-medium mb-6">{currentQuestion.question_text}</h2>

          {/* Options */}
          <div className="space-y-3">
            {currentQuestion.options.map((option) => {
              const isSelected = selectedOption === option.id;
              const revealed = reveals[currentQuestion.id];
              const isCorrect = submitted && revealed && option.id === revealed.correct_option_id;
              const isIncorrect = submitted && isSelected && revealed && option.id !== revealed.correct_option_id;
              
              return (
                <button
                  key={option.id}
                  data-testid={`option-${option.id}`}
                  onClick={() => handleSelectOption(option.id)}
                  disabled={submitted}
                  className={`w-full text-left p-4 rounded-xl border transition-all quiz-option
                    ${isSelected && !submitted ? 'border-primary bg-primary/10' : 'border-border'}
                    ${isCorrect ? 'correct' : ''}
                    ${isIncorrect ? 'incorrect' : ''}
                    ${!submitted ? 'hover:border-primary/50' : ''}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full border flex items-center justify-center text-sm font-medium
                      ${isSelected && !submitted ? 'border-primary bg-primary text-white' : 'border-border'}
                      ${isCorrect ? 'border-success bg-success text-white' : ''}
                      ${isIncorrect ? 'border-destructive bg-destructive text-white' : ''}
                    `}>
                      {option.id.toUpperCase()}
                    </span>
                    <span className="flex-1">{option.text}</span>
                    {isCorrect && <CheckCircle2 className="w-5 h-5 text-success" />}
                    {isIncorrect && <XCircle className="w-5 h-5 text-destructive" />}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Explanation */}
          {submitted && reveals[currentQuestion.id]?.explanation && (
            <div className="mt-6 p-4 rounded-xl bg-primary/10 border border-primary/20">
              <div className="flex items-start gap-2">
                <Lightbulb className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium text-sm mb-1">Explanation</p>
                  <p className="text-sm text-muted-foreground">{reveals[currentQuestion.id].explanation}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        {!submitted ? (
          <Button
            data-testid="submit-answer-btn"
            onClick={handleSubmitAnswer}
            disabled={!selectedOption}
            className="rounded-full px-8"
          >
            Submit Answer
          </Button>
        ) : (
          <Button
            data-testid="next-question-btn"
            onClick={handleNext}
            className="rounded-full px-8"
          >
            {currentIndex < questions.length - 1 ? "Next Question" : "Complete Diagnostic"}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Inline lead-capture form shown on the diagnostic gate. Captures the email
// (adds to VBOUT lead list) BEFORE the visitor heads to /signup, so we don't
// lose them if they bail at the signup step.
const LeadEmailForm = ({ previewWeak, pendingScores, onCaptured }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [captured, setCaptured] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const overall = pendingScores
        ? Object.values(pendingScores).reduce((a, b) => a + b, 0) / Math.max(1, Object.keys(pendingScores).length)
        : null;
      await axios.post(`${API_BASE}/diagnostic/lead`, {
        email: email.trim().toLowerCase(),
        name: name.trim() || null,
        weak_areas: previewWeak,
        overall_score: overall,
        source: "diagnostic_gate",
      });
      // Remember the email so signup can pre-fill
      try {
        localStorage.setItem("passadc_lead_email", email.trim().toLowerCase());
        if (name.trim()) localStorage.setItem("passadc_lead_name", name.trim());
      } catch {}
      setCaptured(true);
      toast.success("Got it! Redirecting to create your free account…");
      setTimeout(() => onCaptured(), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your email. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (captured) {
    return (
      <div className="rounded-full px-4 py-3 bg-primary/10 border border-primary/30 text-primary text-sm font-medium flex items-center justify-center gap-2">
        <CheckCircle2 className="w-4 h-4" /> Redirecting…
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-2" data-testid="lead-capture-form">
      <Input
        data-testid="lead-name-input"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your name (optional)"
        className="text-sm"
      />
      <Input
        data-testid="lead-email-input"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        className="text-sm"
      />
      <Button
        type="submit"
        data-testid="lead-submit-btn"
        size="lg"
        disabled={submitting}
        className="w-full rounded-full py-6 text-base font-semibold glow-primary"
      >
        {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Sparkles className="w-4 h-4 mr-2" />See my full results</>}
      </Button>
    </form>
  );
};

export default DiagnosticPage;
